import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ServiceItem, TimeSlot, WorkingHours } from "@/types";
import { generateTimeSlots } from "@/utils/dateUtils";
import { Skeleton } from "@/components/ui/skeleton";
import { useCalendarSettings } from "@/hooks/useCalendarSettings";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatDuration } from "@/utils/serviceFormatters";

interface Break {
  date: string;
  start_time: string;
  end_time: string;
}

interface RecurringBreakSlot {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  valid_from?: string | null;
  valid_until?: string | null;
}

interface DateTimeSelectionProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  selectedTimeSlot: TimeSlot | null;
  onTimeSlotSelect: (timeSlot: TimeSlot) => void;
  workingHours: WorkingHours[];
  appointments: any[];
  selectedServices: ServiceItem[];
  breaks?: Break[];
  recurringBreaks?: RecurringBreakSlot[];
}

const DateTimeSelection = ({
  selectedDate,
  onDateChange,
  selectedTimeSlot,
  onTimeSlotSelect,
  workingHours,
  appointments,
  selectedServices,
  breaks = [],
  recurringBreaks = [],
}: DateTimeSelectionProps) => {
  const isMobile = useIsMobile();
  const { settings: calendarSettings } = useCalendarSettings();
  const bookingBufferHours = calendarSettings?.booking_buffer_hours ?? 2;

  // Calculate total duration of selected services (including both buffers)
  const getTotalDuration = () => {
    return selectedServices.reduce((total, service) => {
      const match = service.duration.toString().match(/(\d+)/);
      const minutes = match ? parseInt(match[1]) : 0;
      const bufferMinutes = service.buffer_time || 0;
      const bufferBeforeMinutes = service.buffer_time_before || 0;
      return total + minutes + bufferMinutes + bufferBeforeMinutes;
    }, 0);
  };

  // Calculate total buffer before for all services
  const getTotalBufferBefore = () => {
    return selectedServices.reduce((total, service) => {
      return total + (service.buffer_time_before || 0);
    }, 0);
  };

  // Get the working hours for the selected date
  const getWorkingHoursForDate = () => {
    const dayOfWeek = selectedDate.getDay(); // 0 is Sunday, 1 is Monday, etc.
    return workingHours.find((hours) => hours.day_of_week === dayOfWeek);
  };

  // Generate available time slots for the selected date
  const getAvailableTimeSlots = () => {
    const workingHoursForDate = getWorkingHoursForDate();

    if (!workingHoursForDate || !workingHoursForDate.is_working_day) {
      return [];
    }

    const totalDuration = getTotalDuration();
    const totalBufferBefore = getTotalBufferBefore();

    // Use the start_time and end_time directly from the working hours
    return generateTimeSlots(selectedDate, workingHours, appointments, totalDuration, bookingBufferHours, totalBufferBefore, breaks, recurringBreaks);
  };

  // Format time slot for display (only start time for better mobile UX)
  const formatTimeSlot = (slot: TimeSlot) => {
    return slot.startTime;
  };

  // Get working hours status message
  const getWorkingHoursMessage = () => {
    const workingHoursForDate = getWorkingHoursForDate();

    if (!workingHoursForDate) {
      return "Horaires non définies pour ce jour.";
    }

    if (!workingHoursForDate.is_working_day) {
      return "Institut fermé ce jour.";
    }

    return `Ouvert: ${workingHoursForDate.start_time.substring(0, 5)} - ${workingHoursForDate.end_time.substring(0, 5)}`;
  };

  const availableTimeSlots = getAvailableTimeSlots();
  const workingHoursMessage = getWorkingHoursMessage();

  return (
    <div className="flex flex-col space-y-4">
      {/* Calendar - Full width at top */}
      <div className="w-full">
        <div className="rounded-md border shadow-sm p-3 overflow-x-auto">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && onDateChange(date)}
            locale={fr}
            className="mx-auto w-full"
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const dayOfWeek = date.getDay();
              const isPastDate = date < today;
              const workingHoursForDate = workingHours.find((hours) => hours.day_of_week === dayOfWeek);

              // Date passée ou jour non travaillé selon les horaires d'ouverture
              if (isPastDate || !workingHoursForDate?.is_working_day) {
                return true;
              }

              // Vérifier si la date a au moins un créneau disponible
              const totalDuration = getTotalDuration();
              const totalBufferBefore = getTotalBufferBefore();
              const availableSlots = generateTimeSlots(date, workingHours, appointments, totalDuration, bookingBufferHours, totalBufferBefore, breaks, recurringBreaks);

              // Désactiver si aucun créneau disponible
              return availableSlots.length === 0;
            }}
          />
        </div>
      </div>

      {/* Discrete separator with selected date */}
      <div className="relative">
        <Separator className="my-2" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3">
          <span className="text-xs font-medium text-muted-foreground capitalize">
            {format(selectedDate, "EEEE dd MMMM", { locale: fr })}
          </span>
        </div>
      </div>

      {/* Time slots - Full width below */}
      <div className="w-full flex flex-col space-y-3 mt-4">
        {/* Contextual information */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{workingHoursMessage}</span>
          <span className="font-medium">Durée : {formatDuration(getTotalDuration())}</span>
        </div>

        {workingHours.length === 0 ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : availableTimeSlots.length > 0 ? (
          <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pb-0">
            {availableTimeSlots.map((slot, index) => (
              <button
                key={index}
                onClick={() => onTimeSlotSelect(slot)}
                className={`p-2.5 text-sm font-medium rounded-md border transition-all overflow-hidden whitespace-nowrap ${
                  selectedTimeSlot &&
                  selectedTimeSlot.startTime === slot.startTime &&
                  selectedTimeSlot.endTime === slot.endTime
                    ? "bg-bbz-green/30 text-bbz-text border-bbz-green border-l-4 shadow-sm"
                    : "bg-background text-bbz-text border-bbz-green/20 hover:border-bbz-green/50 hover:bg-bbz-beige/30"
                }`}
              >
                {formatTimeSlot(slot)}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center p-4 bg-bbz-beige/20 rounded-md">
            <p className="text-sm text-muted-foreground">Aucun créneau disponible pour cette date.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateTimeSelection;
